import { useCallback, useMemo, useState } from 'react';

import { UsersIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import {
  MODAL_DELETE,
  MODAL_TEAM,
  MODAL_TEAM_MEMBERS,
} from '@/components/organisms/Modals/ModalConductor';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { SimpleFilterCombobox } from '@/components/ui/custom/SimpleFilterCombobox';
import { DataTable } from '@/components/ui/table/data-table';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useInvitations } from '@/hooks/useInvitations';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import { useRoles } from '@/hooks/useRoles';
import { useTeams } from '@/hooks/useTeams';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { Team } from '@/shared/types/team';
import { ADMIN, BUILDER, CHAT_AGENT, CHAT_MANAGER } from '@/util/constants';
import { greekAwareTextMatch } from '@/util/greekFilter';

import { createColumns } from './columns';
import { EmptyTeams } from './EmptyTeams';

/**
 * Teams page component that displays and manages teams.
 *
 * Features:
 * - Displays teams in a sortable table with modern UI
 * - Provides role-based filtering (all, no role, specific roles)
 * - Includes search functionality by team name
 * - Handles team editing, member management, and deletion
 * - Shows permission-based access control for actions
 */
interface TeamsProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const Teams = ({ onEmptyStateChange }: TeamsProps) => {
  useSegments(PageName.MANAGE_TEAM_ACCESS);

  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { teams, deleteTeam, listStatus } = useTeams();
  const { roles } = useRoles();
  const { accountInvitations } = useInvitations();

  // Selectors
  const canWrite = useSelector((state: RootState) => getPermissions(state, 'teams', actions.WRITE));

  // State for filtering and search
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [searchValue, setSearchValue] = useState<string>('');

  // Filter options for role-based filtering
  const filterOptions = useMemo(() => {
    const options = [
      { value: 'all', label: t('roles.filters.all') },
      ...(roles
        ?.filter((r) => [ADMIN, CHAT_AGENT, CHAT_MANAGER, BUILDER].includes(r.role_id))
        .map((r) => ({ label: r.name, value: r.role_id })) ?? []),
      { value: 'no_role', label: t('roles.filters.no_role') },
    ];

    return options;
  }, [roles, t]);

  // Filter data based on role filter and search
  const filteredData = useMemo(() => {
    let filtered = teams || [];

    if (selectedFilter === 'no_role') {
      filtered = filtered.filter(({ role_ids }) => role_ids.length === 0);
    } else if (selectedFilter !== 'all') {
      filtered = filtered.filter(({ role_ids }) => role_ids.includes(selectedFilter));
    }

    if (searchValue) {
      filtered = filtered.filter((team) =>
        team.name ? greekAwareTextMatch(team.name, searchValue) : false
      );
    }

    return filtered;
  }, [teams, selectedFilter, searchValue]);

  // Action handlers
  const handleEditTeam = useCallback(
    (selectedTeam: Team) => {
      trackEvent(EventName.ClickEditAccessTeam);
      dispatch(pushModal(MODAL_TEAM, { team: selectedTeam }));
    },
    [dispatch]
  );

  const handleEditMembers = useCallback(
    (selectedTeam: Team, type: 'add' | 'remove') => {
      dispatch(pushModal(MODAL_TEAM_MEMBERS, { team: selectedTeam, type }));
    },
    [dispatch]
  );

  const handleDeleteTeam = useCallback(
    (selectedTeam: Team) => {
      const deleteProps = {
        subtitle: <Trans i18nKey="teams.delete_subtitle" values={[selectedTeam?.name]} />,
        title: t('teams.delete'),
        confirm: true,
        onDelete: () => {
          deleteTeam(
            { team_id: selectedTeam?.team_id },
            {
              onSuccess: () => {
                dispatch(popModal());
              },
            }
          );
        },
      };
      dispatch(pushModal(MODAL_DELETE, deleteProps));
    },
    [deleteTeam, dispatch, t]
  );

  // Create columns with action handlers
  const columns = useMemo(
    () =>
      createColumns({
        roles,
        accountInvitations,
        canWrite,
        onEditTeam: handleEditTeam,
        onAddMembers: (team) => handleEditMembers(team, 'add'),
        onRemoveMembers: (team) => handleEditMembers(team, 'remove'),
        onDeleteTeam: handleDeleteTeam,
      }),
    [roles, accountInvitations, canWrite, handleEditTeam, handleEditMembers, handleDeleteTeam]
  );

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'teams',
    isLoading,
    isEmpty: teams?.length === 0 && searchValue === '' && selectedFilter === 'all',
  });

  useReportEmptyState(isEmptyPage, onEmptyStateChange);

  return (
    <SettingsContent fill className="pt-5">
      {showEmpty && <EmptyTeams />}

      {showEmptySkeleton && (
        <EmptySkeleton media="icon" descriptionLines={1} actions={canWrite ? 1 : 0} />
      )}

      {showList && (
        <>
          <div className="mb-4 flex items-center justify-between gap-2 pl-1">
            <SearchBar
              placeholder={t('teams.search_placeholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />

            <SimpleFilterCombobox
              options={filterOptions}
              value={selectedFilter}
              onChange={setSelectedFilter}
              icon={<UsersIcon />}
            />
          </div>

          <DataTable.Root
            fill
            className="min-h-0 flex-1"
            columns={columns}
            data={filteredData}
            rowCount={filteredData.length}
            resourceName="team"
            emptyStateMessage={t('teams.no_teams')}
            isLoading={isLoading}
          >
            <DataTable.Content />
            <DataTable.Pagination />
          </DataTable.Root>
        </>
      )}
    </SettingsContent>
  );
};
